import type { SysAppDto } from '@/app/services/api/sysAppService';

/**
 * Rebase a legacy code-less `/workspace/...` routeUrl onto the active tenant's
 * workspace base.
 *
 * Workspace routes are tenant-scoped — `/workspace/{tenantCode}/…` (spec:
 * workspace-tenant-code-routing) — but a `SysApp.routeUrl` is static config
 * authored against the old code-less `/workspace/...` path (e.g. an app alias
 * like `/workspace/tools/simple-notes/?app=business-notes`). When rendering
 * inside a tenant workspace, inject the tenant code from `basePath`. Elsewhere
 * — `/main`, an already-scoped routeUrl, or a non-workspace target — the
 * routeUrl is honored verbatim.
 */
function rebaseRouteUrl(routeUrl: string, basePath: string): string {
  // Only rebase when rendering inside a tenant workspace (basePath carries a code).
  if (!basePath.startsWith('/workspace/')) return routeUrl;
  const isWorkspacePath =
    routeUrl === '/workspace' || routeUrl.startsWith('/workspace/');
  if (!isWorkspacePath) return routeUrl;
  // Already scoped to the active tenant — leave it.
  if (routeUrl === basePath || routeUrl.startsWith(`${basePath}/`)) return routeUrl;
  // Legacy code-less `/workspace/...` — swap the `/workspace` prefix for the
  // tenant-coded base; any query string is preserved by the slice.
  return basePath + routeUrl.slice('/workspace'.length);
}

/**
 * Derive a SysApp's shell route from its classification + appCode, or honor an
 * explicit `routeUrl` override if the DTO carries one — rebased onto the
 * tenant-coded workspace base when inside a tenant workspace.
 *
 * Shared between `AppShell` (desktop drawer items) and `MobileWorkspaceHome`
 * (mobile tile grid) so both surfaces stay aligned when routing conventions
 * evolve. See .kiro/specs/archive/2026-04-18-workspace-mobile-home/design.md D6.
 */
export function toShellRoute(app: SysAppDto, basePath: string): string {
  if (app.routeUrl) return rebaseRouteUrl(app.routeUrl, basePath);
  return app.classification === 'app'
    ? `${basePath}/apps/${app.appCode}`
    : `${basePath}/tools/${app.appCode}`;
}

/**
 * Is `route` — a shell route from `toShellRoute`, possibly carrying an `?app=` alias — the URL we
 * are currently on?
 *
 * ⚠ The `?app=` alias is part of a nav item's IDENTITY, not decoration. Two SysApps can share one
 * path and differ only by it: Business Notes is `…/tools/simple-notes/?app=business-notes` while
 * Simple Notes is `…/tools/simple-notes`. Matching on pathname alone highlighted BOTH at once
 * (reported 2026-07-28) — a bare pathname can never equal a route that carries a query, so the
 * comparison has to split them.
 *
 * `pathname` is expected to come from `useLocationPathname` (trailing slash already normalized);
 * routeUrl values are authored WITH a trailing slash before the query, so normalize here too or
 * nothing ever matches.
 */
export function isShellRouteCurrent(
  route: string,
  pathname: string,
  currentAppAlias: string | null,
): boolean {
  const [rawPath, rawQuery = ''] = route.split('?');
  const routePath = rawPath.length > 1 && rawPath.endsWith('/')
    ? rawPath.replace(/\/+$/, '')
    : rawPath;
  if (routePath !== pathname) return false;
  return (new URLSearchParams(rawQuery).get('app') || null) === (currentAppAlias || null);
}
